import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Crown, ShoppingBag, ArrowRight, Sparkles, CheckCircle2, Shield, Lock, Mail, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { ProductPurchaseDialog } from './ProductPurchaseDialog';
import { PRO_PRICES } from '../data/pricing';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const PremiumPaywall = ({ product, productSlug, previewStep, totalPreviewSteps, onDismiss }) => {
  const navigate = useNavigate();
  const { isEmailVerified, user } = useAuth();
  const [purchaseDialogOpen, setPurchaseDialogOpen] = useState(false);
  const [showVerificationGate, setShowVerificationGate] = useState(false);
  const [sendingVerification, setSendingVerification] = useState(false);
  const [verificationSent, setVerificationSent] = useState(false);

  const handleBuyClick = () => {
    if (!isEmailVerified) {
      setShowVerificationGate(true);
      return;
    }
    setPurchaseDialogOpen(true);
  };

  const handleUpgradeClick = () => {
    if (!isEmailVerified) {
      setShowVerificationGate(true);
      return;
    }
    navigate('/pricing');
  };

  const handleSendVerification = async () => {
    if (!user?.email) return;
    setSendingVerification(true);
    try {
      const res = await fetch(`${API}/api/auth/resend-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: user.email }),
      });
      if (res.status === 429) {
        toast.error('Please wait before requesting another email.');
      } else {
        setVerificationSent(true);
        toast.success('Verification email sent!');
      }
    } catch {
      toast.error('Could not send email. Please try again.');
    } finally {
      setSendingVerification(false);
    }
  };

  // Build product-like object for ProductPurchaseDialog
  const productForDialog = product ? {
    slug: product.slug || productSlug,
    title: product.title,
    priceUsd: product.price_usd,
    priceNok: product.price_nok,
    pageCount: product.pageCount || '',
    type: product.type || '',
  } : null;

  // Verification gate overlay
  if (showVerificationGate) {
    return (
      <div data-testid="verification-gate" className="animate-fade-in-up">
        <div className="bg-white dark:bg-card rounded-2xl border border-stone-100 dark:border-border shadow-lg overflow-hidden max-w-lg mx-auto">
          <div className="bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 px-8 py-8 text-center">
            <div className="w-14 h-14 bg-white dark:bg-card rounded-full flex items-center justify-center mx-auto mb-4 shadow-sm">
              <Mail className="w-7 h-7 text-[#B49A88]" />
            </div>
            <h2 className="text-xl font-serif font-bold text-stone-900 dark:text-foreground">Ett raskt steg</h2>
            <p className="text-sm font-sans text-stone-600 dark:text-muted-foreground mt-2">
              Verify your email to continue with the purchase.
            </p>
          </div>
          <div className="p-6 space-y-4">
            <p className="text-sm font-sans text-stone-500 text-center">
              Vi sender en verifiseringslink til <span className="font-semibold text-stone-700">{user?.email}</span>
            </p>
            <Button
              data-testid="gate-send-verification-btn"
              onClick={handleSendVerification}
              disabled={sendingVerification || verificationSent}
              className="w-full bg-[#B49A88] text-white hover:bg-[#B49A88]/85 shadow-lg transition-all duration-300 font-sans font-semibold px-8 py-5 rounded-full text-base"
            >
              {sendingVerification ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : verificationSent ? (
                <CheckCircle2 className="w-4 h-4 mr-2" />
              ) : (
                <Mail className="w-4 h-4 mr-2" />
              )}
              {verificationSent ? 'Email sendt! Sjekk innboksen' : 'Send verifiseringsmail'}
            </Button>
            <button
              onClick={() => setShowVerificationGate(false)}
              className="w-full text-sm text-stone-500 hover:text-stone-700 font-sans py-2 transition-colors"
            >
              Back
            </button>
            <div className="flex items-center justify-center gap-2 pt-1">
              <Shield className="w-3.5 h-3.5 text-stone-400" />
              <p className="text-[11px] font-sans text-stone-400">Alt du have created er trygt lagret.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="premium-paywall" className="animate-fade-in-up">
      <div className="bg-white dark:bg-card rounded-2xl border border-stone-100 dark:border-border shadow-lg overflow-hidden max-w-lg mx-auto">
        {/* Header */}
        <div className="bg-gradient-to-br from-stone-900 to-stone-800 px-8 py-8 text-center relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-4 left-8 w-20 h-20 rounded-full border border-white/30" />
            <div className="absolute bottom-2 right-6 w-14 h-14 rounded-full border border-white/20" />
          </div>
          <div className="relative">
            <Sparkles className="w-8 h-8 text-amber-300 mx-auto mb-3" />
            <h2 className="text-xl font-serif font-bold text-white">Liker du det du lager?</h2>
            {product && (
              <p className="text-stone-300 text-sm font-sans mt-2">
                Din <span className="text-white font-semibold">{product.title}</span> is ready to be yours.
              </p>
            )}
          </div>
        </div>

        {/* Options */}
        <div className="p-6 space-y-3">
          {/* Buy individual */}
          {productForDialog && (
            <button
              data-testid="paywall-buy-product-btn"
              onClick={handleBuyClick}
              className="w-full flex items-center gap-4 p-4 rounded-2xl border-2 border-stone-100 hover:border-emerald-200 bg-white hover:bg-emerald-50/30 transition-all text-left group"
            >
              <div className="w-12 h-12 rounded-xl bg-emerald-50 flex items-center justify-center flex-shrink-0">
                <ShoppingBag className="w-6 h-6 text-emerald-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-sans font-semibold text-stone-800 text-sm">Buy {product?.title}</p>
                <p className="font-sans text-xs text-stone-500 mt-0.5">One-time purchase · Lifetime access</p>
              </div>
              <span className="font-serif font-bold text-stone-900 text-lg">
                ${product?.price_usd}
              </span>
            </button>
          )}

          {/* Divider */}
          <div className="flex items-center gap-3 py-1">
            <div className="flex-1 h-px bg-stone-100" />
            <span className="text-xs font-sans text-stone-400 uppercase tracking-wider">or</span>
            <div className="flex-1 h-px bg-stone-100" />
          </div>

          {/* Upgrade to Pro */}
          <button
            data-testid="paywall-upgrade-pro-btn"
            onClick={handleUpgradeClick}
            className="w-full flex items-center gap-4 p-4 rounded-2xl border-2 border-primary/20 hover:border-primary/40 bg-primary/5 hover:bg-primary/10 transition-all text-left group"
          >
            <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
              <Crown className="w-6 h-6 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-sans font-semibold text-stone-800 text-sm">Lumera Pro</p>
              <p className="font-sans text-xs text-stone-500 mt-0.5">Unlimited tilgang til alle products</p>
            </div>
            <span className="font-serif font-bold text-primary text-lg">${PRO_PRICES.monthly.usd}<span className="text-xs font-sans text-stone-400 font-normal">/mo</span></span>
          </button>

          {/* Reassurance */}
          <div className="flex items-center justify-center gap-2 pt-3">
            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
            <p className="text-[11px] font-sans text-stone-500">Everything you have created so far is saved.</p>
          </div>
        </div>
      </div>

      {/* Purchase dialog */}
      {productForDialog && (
        <ProductPurchaseDialog
          product={productForDialog}
          open={purchaseDialogOpen}
          onOpenChange={(open) => { if (!open) setPurchaseDialogOpen(false); }}
        />
      )}
    </div>
  );
};

export default PremiumPaywall;
